/**
 * @file Collection Editor - Table view with modal editing for RxDB collections
 * @version 1.0.0
 */

import TableArrayEditor from '../../json-editor/TableArrayEditor.js';
import DocumentEditor from './DocumentEditor.js';

export default {
  name: 'CollectionEditor',

  setup(props, ctx) {
    return {
      DocumentEditor,
    };
  },

  components: {
    TableArrayEditor,
  },

  props: {
    collection: { required: true },
    fields: {
      type: Array,
      default: null,
    },
    excludeFields: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    /**
     * Filtered field names based on fields/excludeFields props
     * By default, excludes non-primitive fields (objects and arrays)
     */
    fieldNames() {
      const itemSchema = this.collection.schema;
      const properties = itemSchema.properties || {};
      const allFields = Object.keys(properties);

      let fieldNames;
      if (this.fields) {
        // If fields is provided, use only those fields (in order)
        fieldNames = this.fields.filter(field => allFields.includes(field));
      } else {
        // By default, exclude non-primitive fields (objects and arrays)
        fieldNames = allFields.filter(field => {
          const fieldSchema = properties[field];
          return fieldSchema.type !== 'object' && fieldSchema.type !== 'array';
        });
      }

      // Apply excludeFields on top
      return fieldNames.filter(field => !this.excludeFields.includes(field));
    },

    /** Derive array schema from collection with filtered fields */
    schema() {
      if (this.$attrs.schema) return this.$attrs.schema;

      const itemSchema = this.collection.schema;
      const properties = itemSchema.properties || {};

      // Build filtered properties
      const filteredProperties = {};
      for (const field of this.fieldNames) {
        filteredProperties[field] = properties[field];
      }

      // Build filtered item schema
      const filteredItemSchema = {
        ...itemSchema,
        properties: filteredProperties,
      };

      // Filter required array if present
      if (itemSchema.required) {
        filteredItemSchema.required = itemSchema.required.filter(field =>
          this.fieldNames.includes(field)
        );
      }

      return { type: 'array', items: filteredItemSchema };
    },

    /** Derive value from collection items as plain data objects */
    value() {
      if (this.$attrs.value) return this.$attrs.value;
      // Map to normalized plain data objects instead of passing reactive document
      // instances. This prevents useEditableObject from serializing reactive docs
      // (which would trigger computed properties including schema validation for
      // every document on every render).
      return this.collection.items.map(doc => doc.$data);
    },
  },

  methods: {
    /**
     * Props for DocumentEditor in modal
     * @param index
     * @param item
     */
    modalEditorProps(index, item) {
      return {
        collection: this.collection,
        document:
          index !== null ? item[this.collection.schema.primaryKey] : null,
        mode: 'form',
        displayLabel: false,
      };
    },

    /**
     * Delete item from collection by primary key
     * @param item
     */
    handleDeleteItem(item) {
      const pk = this.collection.schema.primaryKey;
      this.collection.remove(item[pk]);
    },
  },

  template: `
    <TableArrayEditor
      :schema="schema"
      :value="value"
      :modalEditorProps="modalEditorProps"
      :modalEditorComponent="DocumentEditor"
      :deleteItem="handleDeleteItem"
      :sortable="true"
      :useFilterHeader="true"
      :useFilterBar="true"
      :displayLabel="true"
      :showFilter="true"
      :showPagination="true"
      :movable="false"
      v-bind="$attrs"
    />
    `,
};
